import React, { useState, useEffect, useRef, useMemo } from "react";
import { ReactComponent as MobileLogo } from "../../assets/images/svg/mobile-logo.svg";
import { ReactComponent as MobileSponsorIcon } from "../../assets/images/svg/mobile-sponser.svg";
import { ReactComponent as PlayIcon } from "../../assets/images/svg/play.svg";
import likesButtonSvg from "../../assets/images/svg/likes-button.svg";
import { ReactComponent as CommentSection } from "../../assets/images/svg/comment-section.svg";
import { ReactComponent as FacebookIcon } from "../../assets/images/svg/facebook-icon.svg";
import { ReactComponent as InstagramIcon } from "../../assets/images/svg/instagram-icon.svg";
import { ReactComponent as EnableFb } from "../../assets/images/svg/enable-fb.svg";
import { ReactComponent as DisableFb } from "../../assets/images/svg/disable-fb.svg";
import { ReactComponent as EnableInsta } from "../../assets/images/svg/enable-insta.svg";
import { ReactComponent as DisableInsta } from "../../assets/images/svg/disab-insta.svg";
import { ReactComponent as InstagramLike } from "../../assets/images/svg/insta-like.svg";
import { ReactComponent as InstagramComment } from "../../assets/images/svg/insta-comment.svg";
import { ReactComponent as SpotifyIcon } from "../../assets/images/svg/spotify-icon.svg";
import { ReactComponent as MetaSpotifyLogo } from "../../assets/images/svg/meta-spotify1.svg";
import { ReactComponent as SpotifyPlayButton } from "../../assets/images/svg/spotify-play.svg";
import switcherBg from "../../assets/images/svg/mab-Switcher.svg";
import VideoSelectorDropdown from "./meta-video-selector-dropdown";
import cancelPreiview from "../../assets/images/svg/cancel-preview.svg";
import brandLogo from "../../assets/images/svg/brand-ad-logo.svg";
type VideoItem = {
  id: string;
  fileName: string;
  file?: File;
  isUploading: boolean;
  uploadProgress: number;
  facebookVideoId?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
};

type MobileAdPreviewProps = {
  showPreview: boolean;
  videoFile?: File | null;
  videos?: VideoItem[];
  selectedVideoId?: string | null;
  onVideoSelect?: (videoId: string) => void;
  audioFile?: File | null;
  trackImageUrl?: string | null;
  landingImageFile?: File | null;
  title?: string;
  artistName?: string;
  adText?: string;
  adHeadline?: string;
  spotifyUrl?: string;
  // Meta Ads account names (for edit/draft mode)
  instagramName?: string | null;
  pageName?: string | null;
  // Meta Ads profile image URLs (for edit/draft mode)
  pageImageUrl?: string | null;
  instagramProfileImageUrl?: string | null;
  // Mode flag - if true, use props instead of localStorage
  isEditOrDraftMode?: boolean;
  onClose?: () => void;
};

const MobileAdPreview = ({
  showPreview,
  videoFile,
  videos = [],
  selectedVideoId = null,
  onVideoSelect = () => {},
  audioFile,
  trackImageUrl,
  landingImageFile,
  title,
  artistName,
  adText,
  adHeadline,
  spotifyUrl,
  instagramName,
  pageName,
  pageImageUrl,
  instagramProfileImageUrl,
  isEditOrDraftMode = false,
  onClose,
}: MobileAdPreviewProps) => {
  const [media, setMedia] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<"image" | "video" | null>(null);
  const [thumbnailImage, setThumbnailImage] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<
    "facebook" | "instagram"
  >("facebook");
  const [selectedScreen, setSelectedScreen] = useState<"ad" | "afterClick">(
    "ad",
  );
  const [isAdTextExpanded, setIsAdTextExpanded] = useState(false);
  const previousBlobUrlRef = useRef<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const landingImageUrlRef = useRef<string | null>(null);
  const previousLandingFileRef = useRef<File | null>(null);

  // Determine the current video file based on selection
  const currentVideoFile = useMemo(() => {
    if (!videos || videos.length === 0) {
      return videoFile; // Fallback to old prop for backward compatibility
    }

    const selectedVideo = videos.find((video) => video.id === selectedVideoId);
    if (selectedVideo) {
      return selectedVideo.file;
    }

    // If no selected video, use the first completed video
    const firstCompletedVideo = videos.find((video) => !video.isUploading);
    return firstCompletedVideo?.file || videoFile;
  }, [videos, selectedVideoId, videoFile]);

  // Determine the current video URL (for Facebook videos)
  const currentVideoUrl = useMemo(() => {
    if (!videos || videos.length === 0) {
      return null;
    }

    const selectedVideo = videos.find((video) => video.id === selectedVideoId);
    if (selectedVideo) {
      // Only use videoUrl if it's a Facebook URL (contains facebook.com or fbcdn.net)
      // For our streaming URLs, we'll handle them differently
      const url = selectedVideo.videoUrl || null;
      if (
        url &&
        (url.includes("facebook.com") ||
          url.includes("fbcdn.net") ||
          url.includes("fb.com"))
      ) {
        return url;
      }
      return null; // Don't use streaming URL directly, will fetch as blob
    }

    // If no selected video, use the first completed video
    const firstCompletedVideo = videos.find((video) => !video.isUploading);
    const url = firstCompletedVideo?.videoUrl || null;
    if (
      url &&
      (url.includes("facebook.com") ||
        url.includes("fbcdn.net") ||
        url.includes("fb.com"))
    ) {
      return url;
    }
    return null;
  }, [videos, selectedVideoId]);

  const artworkPreviewUrl = useMemo(() => {
    // Prefer the uploaded Landing Page artwork for the after-click screen.
    // `landingImageUrlRef.current` is set from `landingImageFile`.
    if (landingImageFile && landingImageUrlRef.current) {
      return landingImageUrlRef.current;
    }
    if (trackImageUrl) return trackImageUrl;
    if (thumbnailImage) return thumbnailImage;
    if (mediaType === "image" && media) return media;
    return null;
  }, [landingImageFile, trackImageUrl, thumbnailImage, mediaType, media]);

  // Get meta ads account data from sessionStorage (same as used in meta-new-compaing.tsx)
  const [metaAdsAccountData, setMetaAdsAccountData] = useState<{
    page: { name: string; image: string | null } | null;
    instagram: { name: string; image: string | null } | null;
    adAccount: { name: string; id: string } | null;
  } | null>(null);

  // Read from sessionStorage and listen for changes
  useEffect(() => {
    const readFromStorage = () => {
      if (typeof window === "undefined") return;
      try {
        const raw = localStorage.getItem("metaAdsAccountData");
        if (!raw) {
          setMetaAdsAccountData(null);
          return;
        }

        const parsed = JSON.parse(raw);
        // The sessionStorage structure now includes images for page and instagram
        setMetaAdsAccountData({
          page: parsed?.page
            ? {
                name: parsed.page.name,
                image: parsed.page.image || null,
              }
            : null,
          instagram: parsed?.instagram
            ? {
                name: parsed.instagram.name,
                image: parsed.instagram.image || null,
              }
            : null,
          adAccount: parsed?.adAccount
            ? {
                name: parsed.adAccount.name,
                id: parsed.adAccount.id,
              }
            : null,
        });
      } catch (e) {
        console.error("Error parsing metaAdsSelections:", e);
        setMetaAdsAccountData(null);
      }
    };

    // Initial read
    readFromStorage();

    // Listen for storage events (cross-tab/window)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "metaAdsAccountData") {
        readFromStorage();
      }
    };

    // Listen for custom events (same-tab updates from modal)
    const handleCustomEvent = () => {
      readFromStorage();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("metaAdsSelectionsChanged", handleCustomEvent);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("metaAdsSelectionsChanged", handleCustomEvent);
    };
  }, []);

  // Get account image and name based on selected platform
  const accountImage = useMemo(() => {
    // In edit/draft mode, use props (from API response) instead of localStorage
    if (isEditOrDraftMode) {
      if (selectedPlatform === "instagram") {
        return instagramProfileImageUrl || null;
      }
      if (selectedPlatform === "facebook") {
        return pageImageUrl || null;
      }
      return null;
    }

    // Create mode - use localStorage/sessionStorage
    // If Instagram is selected, use Instagram account image
    if (selectedPlatform === "instagram") {
      if (metaAdsAccountData?.instagram?.image) {
        return metaAdsAccountData.instagram.image;
      }
    }
    // If Facebook is selected, use Facebook page image
    if (selectedPlatform === "facebook") {
      if (metaAdsAccountData?.page?.image) {
        return metaAdsAccountData.page.image;
      }
    }
    // Fallback to user profile image
  }, [selectedPlatform, metaAdsAccountData, isEditOrDraftMode, instagramProfileImageUrl, pageImageUrl]);

  const accountName = useMemo(() => {
    // In edit/draft mode, only use props (from API response)
    if (isEditOrDraftMode) {
      if (selectedPlatform === "instagram") {
        return instagramName || null;
      }
      if (selectedPlatform === "facebook") {
        return pageName || null;
      }
      return null;
    }

    // Create mode - use localStorage/sessionStorage
    // If Instagram is selected, use Instagram account name
    if (selectedPlatform === "instagram") {
      // Priority: Props > SessionStorage > Username > Default
      if (instagramName) return instagramName;
      if (metaAdsAccountData?.instagram?.name) {
        return metaAdsAccountData.instagram.name;
      }
    }
    // If Facebook is selected, use Facebook page name
    if (selectedPlatform === "facebook") {
      // Priority: Props > SessionStorage > Username > Default
      if (pageName) return pageName;
      if (metaAdsAccountData?.page?.name) {
        return metaAdsAccountData.page.name;
      }
    }
  }, [selectedPlatform, metaAdsAccountData, instagramName, pageName, isEditOrDraftMode]);

  useEffect(() => {
    const loadVideo = async () => {
      if (previousBlobUrlRef.current) {
        URL.revokeObjectURL(previousBlobUrlRef.current);
        previousBlobUrlRef.current = null;
      }
      // Only revoke landing URL if landing file changed, not just video
      if (previousLandingFileRef.current !== landingImageFile && landingImageUrlRef.current) {
        URL.revokeObjectURL(landingImageUrlRef.current);
        landingImageUrlRef.current = null;
      }

      // Setup landing image as thumbnail
      if (landingImageFile) {
        const url = URL.createObjectURL(landingImageFile);
        landingImageUrlRef.current = url;
        setThumbnailImage(url);
      } else {
        setThumbnailImage(null);
      }

      // Setup video URL - handle File, Facebook URL, or streaming URL
      if (currentVideoFile) {
        // Local video file
        const url = URL.createObjectURL(currentVideoFile);
        previousBlobUrlRef.current = url;
        setVideoUrl(url);
        finalizeMediaSetup(url);
      } else if (currentVideoUrl) {
        // Facebook video URL (public, no auth needed)
        setVideoUrl(currentVideoUrl);
        finalizeMediaSetup(currentVideoUrl);
      } else {
        // Check if there's a streaming URL from S3 (our draft videos)
        const selectedVideo = videos?.find((v) => v.id === selectedVideoId);
        const firstCompletedVideo = videos?.find((v) => !v.isUploading);
        const videoToLoad = selectedVideo || firstCompletedVideo;

        if (videoToLoad?.videoUrl && !videoToLoad.file) {
          // This is a streaming URL from S3, fetch it as blob
          try {
            const token = localStorage.getItem("token");
            const response = await fetch(videoToLoad.videoUrl, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (!response.ok) {
              throw new Error(`Failed to fetch video: ${response.statusText}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            previousBlobUrlRef.current = url;
            setVideoUrl(url);
            finalizeMediaSetup(url);
          } catch (error) {
            console.error("Error loading streaming video:", error);
            setVideoUrl(null);
            setMedia(null);
            setMediaType(null);
            setIsPlaying(false);
          }
        } else {
          setVideoUrl(null);
          setMedia(null);
          setMediaType(null);
          setIsPlaying(false);
        }
      }
    };

    const finalizeMediaSetup = (videoUrlForMedia: string | null) => {
      // Determine what to show initially
      // Landing page takes priority - always show it if available
      if (landingImageFile) {
        setMedia(landingImageUrlRef.current);
        setMediaType("image");
        setIsPlaying(false);
      } else if (videoUrlForMedia) {
        // Video (local, Facebook, or streamed)
        setMedia(videoUrlForMedia);
        setMediaType("video");
        setIsPlaying(false);
      } else {
        // No media
        setMedia(null);
        setMediaType(null);
        setIsPlaying(false);
      }
    };

    loadVideo();

    return () => {
      if (previousBlobUrlRef.current) {
        URL.revokeObjectURL(previousBlobUrlRef.current);
        previousBlobUrlRef.current = null;
      }
      // Only revoke landing URL if landing file changed, not just video
      if (previousLandingFileRef.current !== landingImageFile && landingImageUrlRef.current) {
        URL.revokeObjectURL(landingImageUrlRef.current);
        landingImageUrlRef.current = null;
      }
      previousLandingFileRef.current = landingImageFile ?? null;
    };
  }, [
    currentVideoFile,
    currentVideoUrl,
    landingImageFile,
    videos,
    selectedVideoId,
  ]);

  const handlePlayClick = () => {
    // If both thumbnail and video exist, switch to video
    if (thumbnailImage && videoUrl && mediaType === "image") {
      setMedia(videoUrl);
      setMediaType("video");
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play();
          setIsPlaying(true);
        }
      }, 100);
    } else if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
    // Don't switch to thumbnail - keep video frame visible when paused
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
    // Switch back to thumbnail when video ends
    if (thumbnailImage && videoUrl) {
      setMedia(thumbnailImage);
      setMediaType("image");
    }
  };

  // Video time tracking handlers
  const handleTimeUpdate = () => {
    if (videoRef.current && !isSeeking) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleProgress = () => {
    if (videoRef.current && videoRef.current.buffered.length > 0) {
      const bufferedEnd = videoRef.current.buffered.end(
        videoRef.current.buffered.length - 1,
      );
      setBuffered(bufferedEnd);
    }
  };

  const handleVideoToggle = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!videoRef.current) return;

    const timeline = e.currentTarget;
    const rect = timeline.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const seekTime = pos * duration;

    videoRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Audio playback handlers
  const handleAudioPlay = () => {
    if (audioRef.current) {
      if (isAudioPlaying) {
        audioRef.current.pause();
        setIsAudioPlaying(false);
      } else {
        audioRef.current.play();
        setIsAudioPlaying(true);
      }
    }
  };

  const handleAudioEnded = () => {
    setIsAudioPlaying(false);
  };

  // Setup audio element URL
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  useEffect(() => {
    if (audioFile) {
      const url = URL.createObjectURL(audioFile);
      setAudioUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setAudioUrl(null);
    }
  }, [audioFile]);

  // Pause audio when leaving "afterClick" screen
  useEffect(() => {
    if (selectedScreen !== "afterClick" && isAudioPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsAudioPlaying(false);
    }
  }, [selectedScreen, isAudioPlaying]);

  // Show landing image when switching to "afterClick" screen
  useEffect(() => {
    if (selectedScreen === "afterClick" && landingImageFile) {
      setMedia(landingImageUrlRef.current);
      setMediaType("image");
      setIsPlaying(false);
    }
  }, [selectedScreen, landingImageFile]);

  if (!showPreview) return null;

  return (
    <>
      {/* Hidden audio element */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={handleAudioEnded}
          style={{ display: "none" }}
        />
      )}

      {/*
        Outermost wrapper keeps clipping on mobile and allows overflow on desktop.
      */}
      <div
        className="relative overflow-hidden preview-scaler"
        style={{ borderRadius: "20px" }}
      >
        {/* ── MOBILE HEADER: Title + Close Button ─────────────────────────── */}
        <div className="lg:hidden absolute top-4 left-0 w-full z-30 px-4 py-3 flex items-center justify-between">
          {/* Title */}
          <h2 className="text-white text-[24px] font-semibold">
            Mockup Preview
          </h2>

          {/* Close Button */}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="flex items-center justify-center"
            >
              <img
                src={cancelPreiview}
                alt="Close preview"
                className="w-[15px] h-[15px]"
              />
            </button>
          )}
        </div>

        {/* ── ALL EXISTING CONTENT ──── */}
        <style>{`
    @media (min-width: 1024px) {
      .preview-scaler {
        transform: scale(0.88);
      }
    }
    @media (max-width: 660px) {
      .preview-scaler {
        transform: scale(0.82);
      }
    }
    .mockup-screen::-webkit-scrollbar {
      display: none;
    }
  `}</style>
        <div style={{ position: "relative", zIndex: 1, overflow: "visible" }}>
          <div
            className="flex flex-col items-center justify-center preview-scaler"
            style={{
              width: "100%",
              maxWidth: "100%",
              minWidth: "unset",
              transformOrigin: "center center",
            }}
          >
            <div
              className="flex flex-col items-center bg-[#878DA11A] py-5 px-4 rounded-lg w-[400px] relative"
              style={{ borderRadius: "20px" }}
            >
              {/* Video Selector and Social Media Buttons - Only show on "Screen of ad" */}
              {selectedScreen === "ad" && (
                <div className="flex items-center gap-4 sm:gap-8 lg:gap-[120px] mb-4 w-full justify-center">
                  {/* Video Selector Dropdown */}
                  {videos && videos.length > 0 && onVideoSelect && (
                    <VideoSelectorDropdown
                      videos={videos}
                      selectedVideoId={selectedVideoId}
                      onVideoSelect={onVideoSelect}
                      className="w-[180px]"
                    />
                  )}

                  {/* Facebook/Instagram Buttons */}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setSelectedPlatform("facebook")}
                      className="flex items-center justify-center"
                    >
                      {selectedPlatform === "facebook" ? (
                        <EnableFb className="w-auto h-auto" />
                      ) : (
                        <DisableFb className="w-auto h-auto" />
                      )}
                    </button>
                    {/* Vertical separator line */}
                    <div className="h-6 w-px bg-[#878DA1] opacity-50" />
                    <button
                      onClick={() => setSelectedPlatform("instagram")}
                      className="flex items-center justify-center"
                    >
                      {selectedPlatform === "instagram" ? (
                        <EnableInsta className="w-auto h-auto" />
                      ) : (
                        <DisableInsta className="w-auto h-auto" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              <div
                className="relative"
                style={{
                  width: "330px",
                  height: "680px",
                  borderRadius: "45px",
                  padding: "10px",
                  background: "#0f0f0f",
                  boxShadow: `
              0 0 0 1px rgba(244,245,240,0.85),
              inset 0 0 0 1px rgba(175,176,171,0.45),
              0 0 50px rgba(0,0,0,0.45)
            `,
                }}
              >
                {/* SCREEN */}
                <div
                  className={`relative mockup-screen  ${
                    selectedScreen === "afterClick"
                      ? "overflow-hidden"
                      : "overflow-y-auto bg-[#f2f2f2]"
                  }`}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "35px",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    ...(selectedScreen === "afterClick" && {
                      backgroundColor: "#000000",
                    }),
                  }}
                >
                  {/* After-click: blurred artwork background is rendered as an overlay layer below */}
                  {selectedScreen === "afterClick" && artworkPreviewUrl && (
                    <div className="absolute inset-0 z-0">
                      <div
                        className="absolute inset-0 bg-center bg-cover"
                        style={{
                          backgroundImage: `url(${artworkPreviewUrl})`,
                          transform: "scale(1.25)",
                          filter: "blur(15px)",
                        }}
                      />
                      <div className="absolute inset-0 bg-black/35" />
                      <div
                        className="absolute left-0 right-0"
                        style={{
                          top: "335px",
                          bottom: 0,
                          background:
                            "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #020202 60%)",
                        }}
                      />
                    </div>
                  )}

                  {/* 🔵 Dynamic Island */}
                  <div
                    className="absolute left-1/2 -translate-x-1/2"
                    style={{
                      top: "10px",
                      width: "80px",
                      height: "20px",
                      backgroundColor: "black",
                      borderRadius: "20px",
                      zIndex: 2,
                    }}
                  />

                  {/* TOP GRAY SPACING — afterClick: transparent so full-height gradient shows */}
                  <div
                    className={`w-full h-10 mt-9 ${selectedScreen === "afterClick" ? "bg-transparent" : "bg-transparent"}`}
                  />

                  {/* MAIN FB CARD */}
                  {selectedScreen === "afterClick" ? (
                    <div className="relative z-[1] w-full h-[calc(100%-80px)] flex flex-col items-center pb-4">
                      <div className="mt-2 mb-4 flex items-center justify-center">
                        <img
                          src={brandLogo}
                          alt="PitchPlaylists"
                          className="h-[35px]  mt-[-40px] opacity-95"
                        />
                      </div>

                      <div className="w-[310px]  overflow-hidden mt-[-12px] bg-transparent">
                        <div
                          className="relative w-full bg-black overflow-hidden"
                          style={{ aspectRatio: "1/1" }}
                          onClick={audioFile ? handleAudioPlay : undefined}
                        >
                          {(artworkPreviewUrl || media) && (
                            <img
                              src={artworkPreviewUrl || media || ""}
                              alt="After click media preview"
                              className="w-full h-full object-cover object-center"
                            />
                          )}

                          {!artworkPreviewUrl && !media && (
                            <div className="w-full h-full flex items-center justify-center bg-[#1F2733]">
                              <span className="text-white text-sm opacity-50">
                                No media uploaded
                              </span>
                            </div>
                          )}

                          {media && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAudioPlay();
                                }}
                                aria-label={
                                  isAudioPlaying ? "Pause audio" : "Play audio"
                                }
                                className="w-[120px] h-[120px]  flex items-center justify-center"
                              >
                                {isAudioPlaying ? (
                                  <svg
                                    className="w-[34px] h-[34px] text-white"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <rect
                                      x="6"
                                      y="4"
                                      width="4"
                                      height="16"
                                      rx="1"
                                    />
                                    <rect
                                      x="14"
                                      y="4"
                                      width="4"
                                      height="16"
                                      rx="1"
                                    />
                                  </svg>
                                ) : (
                                  <PlayIcon className="w-[44px] h-[44px] text-white" />
                                )}
                              </button>
                            </div>
                          )}
                        </div>

                        <div className=" bg-transparent">
                          <div className="px-4 py-4 text-center ">
                            <h4 className="text-white font-normal leading-[130%] text-[16px] mb-1 ">
                              {title || "Chains of Love"}
                            </h4>
                            <p className="text-white/70 text-[14px]">
                              {artistName || "Charli xcx"}
                            </p>
                          </div>

                          <div className="px-4 py-4  border-white/10">
                            <div className="flex items-center justify-between gap-2">
                              <MetaSpotifyLogo className="w-[92px] h-auto flex-shrink-0" />
                              <button
                                type="button"
                                onClick={() =>
                                  spotifyUrl &&
                                  window.open(spotifyUrl, "_blank")
                                }
                                className="flex items-center justify-center"
                              >
                                <SpotifyPlayButton className="w-[108px] h-[35px]" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-center space-y-[4px] mb-[3px]">
                        <p className="text-white/60 text-[12px] font-semibold">
                          Terms
                        </p>
                        <p className="text-white/60 text-[12px] font-semibold">
                          Privacy
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* REGULAR AD VIEW */
                    <div
                      className={`w-[300px] mx-auto bg-white border border-gray-300 ${selectedPlatform === "instagram" ? "mb-2" : "mb-8"}`}
                      style={{ marginTop: "-15px" }}
                    >
                      {/* HEADER */}
                      <div className="flex justify-between px-3 pt-3 pb-2 bg-white">
                        <div className="flex gap-2">
                          {/* Account Image or Logo */}
                          <div
                            className="w-10 h-10 rounded-full bg-black flex items-center justify-center"
                            style={{ overflow: "hidden" }}
                          >
                            {accountImage ? (
                              <img
                                src={accountImage}
                                alt="Profile"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <MobileLogo
                                width="24"
                                height="24"
                                style={{
                                  display: "block",
                                  width: "24px",
                                  height: "24px",
                                }}
                              />
                            )}
                          </div>

                          <div className="flex flex-col leading-tight">
                            <span className="font-semibold text-[14px]">
                              {accountName}
                            </span>
                            <div className="flex items-center gap-1 text-gray-500 text-[12px]">
                              <span>Sponsored</span>
                              <span className="text-[20px] leading-none">
                                ·
                              </span>
                              <MobileSponsorIcon className="w-[14px] h-[14px]" />
                            </div>
                          </div>
                        </div>

                        <div className="text-[24px] leading-none">
                          {selectedPlatform === "instagram" ? "−" : "⋯"}
                        </div>
                      </div>

                      {/* MAIN TEXT */}
                      {selectedPlatform === "facebook" &&
                        adText &&
                        adText.trim() !== "" && (
                          <div className="px-3 pb-2">
                            {!isAdTextExpanded ? (
                              <>
                                <p className="text-[14px] break-words line-clamp-2">
                                  {adText}
                                </p>
                                {adText.length > 80 && (
                                  <button
                                    onClick={() => setIsAdTextExpanded(true)}
                                    className="text-[14px] text-gray-500 hover:underline mt-1"
                                  >
                                    See more
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                <p className="text-[14px] break-words">
                                  {adText}
                                </p>
                                <button
                                  onClick={() => setIsAdTextExpanded(false)}
                                  className="text-[14px] text-gray-500 hover:underline mt-1"
                                >
                                  See less
                                </button>
                              </>
                            )}
                          </div>
                        )}

                      {/* MEDIA - 1:1 aspect ratio */}
                      <div
                        className="relative w-full bg-black overflow-hidden cursor-pointer"
                        style={{ aspectRatio: "1/1" }}
                        onClick={
                          mediaType === "video" ? handleVideoToggle : undefined
                        }
                      >
                        {mediaType === "image" && media && (
                          <img
                            src={media}
                            className="w-full h-full object-cover"
                          />
                        )}

                        {mediaType === "video" && media && (
                          <>
                            <video
                              ref={videoRef}
                              src={media}
                              className="w-full h-full object-cover"
                              controls={false}
                              onPlay={handleVideoPlay}
                              onPause={handleVideoPause}
                              onEnded={handleVideoEnded}
                              onTimeUpdate={handleTimeUpdate}
                              onLoadedMetadata={handleLoadedMetadata}
                              onProgress={handleProgress}
                            />

                            {/* Custom Video Controls */}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2 pb-6">
                              <div className="flex items-center gap-2">
                                {/* Play/Pause Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (videoRef.current) {
                                      if (isPlaying) {
                                        videoRef.current.pause();
                                      } else {
                                        videoRef.current.play();
                                      }
                                    }
                                  }}
                                  className="w-8 h-8 flex items-center justify-center text-white hover:scale-110 transition-transform"
                                >
                                  {isPlaying ? (
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 12 12"
                                      fill="currentColor"
                                    >
                                      <rect x="0" y="0" width="4" height="12" />
                                      <rect x="6" y="0" width="4" height="12" />
                                    </svg>
                                  ) : (
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 12 12"
                                      fill="currentColor"
                                    >
                                      <path d="M0 0L10 6L0 12V0Z" />
                                    </svg>
                                  )}
                                </button>

                                {/* Timeline */}
                                <div
                                  className="flex-1 h-1 bg-white/30 rounded-full cursor-pointer relative"
                                  onClick={handleSeek}
                                >
                                  {/* Buffered portion */}
                                  <div
                                    className="absolute h-full bg-white/50 rounded-full"
                                    style={{
                                      width: `${(buffered / duration) * 100}%`,
                                    }}
                                  />
                                  {/* Played portion */}
                                  <div
                                    className="absolute h-full bg-[#58F2A9] rounded-full"
                                    style={{
                                      width: `${(currentTime / duration) * 100 || 0}%`,
                                    }}
                                  />
                                </div>

                                {/* Time Display */}
                                <span
                                  className="text-white text-[10px] font-medium whitespace-nowrap"
                                  style={{ fontFamily: "Poppins, sans-serif" }}
                                >
                                  {formatTime(currentTime)} /{" "}
                                  {formatTime(duration)}
                                </span>
                              </div>
                            </div>
                          </>
                        )}

                        {/* Show placeholder only if no media */}
                        {!media && (
                          <div className="w-full h-full flex items-center justify-center bg-[#1F2733]">
                            <span className="text-white text-sm opacity-50">
                              No media uploaded
                            </span>
                          </div>
                        )}

                        {/* WHITE PLAY ICON - Show for videos that are not playing */}
                        {!isPlaying && mediaType === "video" && media && (
                          <div
                            className="absolute inset-0 flex items-center justify-center cursor-pointer"
                            onClick={handlePlayClick}
                          >
                            <div className="w-[120px] h-[120px] border-white flex items-center justify-center">
                              <PlayIcon className="w-[70px] h-[70px] text-white" />
                            </div>
                          </div>
                        )}

                        {mediaType === "image" && media && videoUrl && (
                          <div
                            className="absolute inset-0 flex items-center justify-center cursor-pointer"
                            onClick={handlePlayClick}
                          >
                            <div className="w-[120px] h-[120px] border-white flex items-center justify-center">
                              <PlayIcon className="w-[58px] h-[58px] text-white" />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* BOTTOM SECTION - Only show for Facebook */}
                      {selectedPlatform === "facebook" && (
                        <div className="flex justify-between items-center gap-2 px-4 py-3 border-t bg-white">
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-500 text-xs">
                              pitchplaylists.com
                            </p>
                            <p className="font-semibold text-sm break-words line-clamp-2">
                              {adHeadline || "Listen now on Spotify!"}
                            </p>
                          </div>

                          <button className="bg-gray-200 px-4 py-2 rounded-lg text-xs font-semibold transition-opacity cursor-pointer flex-shrink-0">
                            Listen Now
                          </button>
                        </div>
                      )}

                      {/* REACTIONS */}
                      {selectedPlatform === "facebook" ? (
                        <div className="px-4 py-2 flex justify-between border-t bg-white">
                          <div className="flex items-center gap-1">
                            <img
                              src={likesButtonSvg}
                              alt=""
                              role="presentation"
                              className="flex-shrink-0 object-contain w-[70px] mt-[-1px] h-full"
                            />
                          </div>
                          <div className="flex gap-1 text-[12px] text-gray-600">
                            <span>12 Comments</span>
                            <span>8 Shares</span>
                          </div>
                        </div>
                      ) : (
                        <div className="border-t w-full flex items-center justify-start cursor-pointer transition-opacity">
                          <InstagramLike className="w-[300px] h-[40px]" />
                        </div>
                      )}

                      {selectedPlatform === "instagram" && (
                        <div className="flex justify-around items-center py-1 text-gray-700 text-[12px] font-medium">
                          <InstagramComment height="45" />
                        </div>
                      )}
                      {selectedPlatform === "instagram" &&
                        adText &&
                        adText.trim() !== "" && (
                          <div className="px-3 pb-1">
                            {!isAdTextExpanded ? (
                              <>
                                <p className="text-[14px] break-words line-clamp-2">
                                  {adText}
                                </p>
                                {adText.length > 80 && (
                                  <button
                                    onClick={() => setIsAdTextExpanded(true)}
                                    className="text-[14px] text-gray-500 hover:underline mt-1"
                                  >
                                    See more
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                <p className="text-[14px] break-words">
                                  {adText}
                                </p>
                                <button
                                  onClick={() => setIsAdTextExpanded(false)}
                                  className="text-[14px] text-gray-500 hover:underline mt-1"
                                >
                                  See less
                                </button>
                              </>
                            )}
                          </div>
                        )}

                      {/* LINE - Only show for Facebook */}
                      {selectedPlatform === "facebook" && (
                        <div className="border-b"></div>
                      )}

                      {selectedPlatform === "facebook" && (
                        <div className="flex justify-around items-center py-2 text-gray-700 text-[12px] font-medium bg-white">
                          <CommentSection width="300" height="45" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ── MOBILE TOGGLE BUTTONS (below phone mockup) ── */}
              <div
                className="relative flex items-center p-1 rounded-full lg:hidden mt-5"
                style={{
                  width: "360px",
                  height: "68px",
                  background: "rgba(255, 255, 255, 0.10)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  borderRadius: "999px",
                  padding: "4px",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                }}
              >
                <button
                  onClick={() => setSelectedScreen("ad")}
                  className="rounded-full transition-all duration-300 flex items-center justify-center"
                  style={{
                    width: "155px",
                    height: "54px",
                    background:
                      selectedScreen === "ad" ? "#FFFFFF" : "transparent",
                    color: selectedScreen === "ad" ? "#000000" : "#FFFFFF",
                    borderRadius: "999px",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    fontStyle: "normal",
                    fontSize: "16px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                    padding: 0,
                    marginLeft: 4,
                  }}
                >
                  Screen of ad
                </button>

                <button
                  onClick={() => setSelectedScreen("afterClick")}
                  className="rounded-full transition-all duration-300 flex items-center justify-center"
                  style={{
                    width: "190px",
                    height: "54px",
                    background:
                      selectedScreen === "afterClick"
                        ? "#FFFFFF"
                        : "transparent",
                    color:
                      selectedScreen === "afterClick" ? "#000000" : "#FFFFFF",
                    borderRadius: "999px",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    fontStyle: "normal",
                    fontSize: "16px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                    padding: 0,
                    margin: 0,
                  }}
                >
                  After click
                </button>
              </div>
            </div>

            {/* ── DESKTOP TOGGLE BUTTONS ── */}
            <div
              className="mt-2 hidden lg:flex items-center justify-center bg-[#878DA11A] h-[95px] w-full"
              style={{ borderRadius: "20px" }}
            >
              <div
                className="hidden lg:flex relative flex items-center p-1 rounded-full"
                style={{
                  width: "360px",
                  height: "68px",
                  background: "rgba(255, 255, 255, 0.10)",

                  borderRadius: "999px",
                  padding: "4px",
                  transition: "background 0.3s ease",
                }}
              >
                <button
                  onClick={() => setSelectedScreen("ad")}
                  className="rounded-full transition-all duration-300 flex items-center justify-center"
                  style={{
                    width: "155px",
                    height: "54px",
                    background:
                      selectedScreen === "ad" ? "#FFFFFF" : "transparent",
                    color: selectedScreen === "ad" ? "#000000" : "#FFFFFF",
                    borderRadius: "999px",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    fontStyle: "normal",
                    fontSize: "16px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                    padding: 0,
                    marginLeft: 4,
                  }}
                >
                  Screen of ad
                </button>

                <button
                  onClick={() => setSelectedScreen("afterClick")}
                  className="rounded-full transition-all duration-300 flex items-center justify-center"
                  style={{
                    width: "190px",
                    height: "54px",
                    background:
                      selectedScreen === "afterClick"
                        ? "#FFFFFF"
                        : "transparent",
                    color:
                      selectedScreen === "afterClick" ? "#000000" : "#FFFFFF",
                    borderRadius: "999px",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    fontStyle: "normal",
                    fontSize: "16px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                    padding: 0,
                    margin: 0,
                  }}
                >
                  Screen after ad click
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* ── END EXISTING CONTENT ─────────────────────────────────────────── */}
      </div>
    </>
  );
};

export default MobileAdPreview;
